import React from "react";
import { cn } from "./cn";

// whatsappFormat — convierte el marcado de WhatsApp a JSX seguro.
//
//   *negrita*   → <strong>      _cursiva_   → <em>
//   ~tachado~   → <s>           ```mono```  → <code>
//
// El parser es por tokens: NUNCA se construye HTML ni se usa
// dangerouslySetInnerHTML, así que el contenido del cliente no puede inyectar
// markup. Reglas replicadas de WhatsApp:
//   · el delimitador solo vale si abre y cierra en la MISMA línea,
//   · no puede haber espacio justo después del de apertura ni justo antes del
//     de cierre,
//   · se puede anidar (`*_ambos_*`) pero un delimitador no se anida en sí mismo,
//   · un delimitador sin pareja se muestra literal (`3 * 4 = 12`).
//
// El resaltado de búsqueda se aplica DENTRO del parser (no sobre el resultado):
// se calcula el texto plano del árbol, se buscan ahí las coincidencias y se
// parten las hojas de texto al emitir. Así una coincidencia que cruza un
// delimitador (`ho*la*`) se sigue contando una sola vez y ninguna etiqueta se
// rompe.

const MARK_TAGS = { "*": "strong", _: "em", "~": "s" };
const FENCE = "```";
const NO_MATCHES = [];
const NO_MARKS = [];

function isSpaceChar(ch) {
  return ch === " " || ch === "\t" || ch === "\n" || ch === "\r";
}

// Índice del delimitador de cierre válido para `ch` abierto en openIdx, o -1.
function findMarkClose(text, openIdx, ch) {
  const afterOpen = text[openIdx + 1];
  if (afterOpen === undefined || isSpaceChar(afterOpen)) return -1;
  for (let j = openIdx + 2; j < text.length; j += 1) {
    const c = text[j];
    if (c === "\n") return -1;
    if (c === ch && !isSpaceChar(text[j - 1])) return j;
  }
  return -1;
}

// Índice del ``` de cierre buscando desde `from`, o -1. El bloque debe cerrar
// en la misma línea y tener al menos un carácter dentro.
function findFenceClose(text, from) {
  for (let j = from + 1; j + FENCE.length <= text.length; j += 1) {
    if (text[j] === "\n") return -1;
    if (text.startsWith(FENCE, j)) return j;
  }
  return -1;
}

// Árbol de nodos: { type: 'text', value } | { type: 'strong'|'em'|'s'|'code', children }
function tokenize(text, openMarks) {
  const nodes = [];
  let buffer = "";

  const flush = () => {
    if (buffer) {
      nodes.push({ type: "text", value: buffer });
      buffer = "";
    }
  };

  let i = 0;
  while (i < text.length) {
    if (text.startsWith(FENCE, i)) {
      const close = findFenceClose(text, i + FENCE.length);
      if (close !== -1) {
        flush();
        // El contenido de ```…``` es literal: no se re-parsea (igual que WA).
        nodes.push({
          type: "code",
          children: [{ type: "text", value: text.slice(i + FENCE.length, close) }],
        });
        i = close + FENCE.length;
        continue;
      }
    }

    const ch = text[i];
    const tag = MARK_TAGS[ch];
    if (tag && openMarks.indexOf(ch) === -1) {
      const close = findMarkClose(text, i, ch);
      if (close !== -1) {
        flush();
        nodes.push({
          type: tag,
          children: tokenize(text.slice(i + 1, close), openMarks.concat(ch)),
        });
        i = close + 1;
        continue;
      }
    }

    buffer += ch;
    i += 1;
  }

  flush();
  return nodes;
}

function plainOf(nodes) {
  let out = "";
  for (const node of nodes) {
    out += node.type === "text" ? node.value : plainOf(node.children);
  }
  return out;
}

/**
 * Rangos [inicio, fin) de todas las coincidencias de `query` en `text`,
 * sin solaparse y sin distinguir mayúsculas.
 * @param {string} text
 * @param {string} query
 * @returns {Array<[number, number]>}
 */
export function findMatches(text, query) {
  const out = [];
  if (!text || !query) return out;
  const needle = String(query);
  const lowerText = text.toLowerCase();
  const lowerNeedle = needle.toLowerCase();
  // toLowerCase puede cambiar la longitud en alfabetos raros (ej. "İ"): ahí los
  // offsets dejarían de coincidir con el texto original, así que se compara
  // respetando mayúsculas antes que resaltar el fragmento equivocado.
  const caseInsensitive =
    lowerText.length === text.length && lowerNeedle.length === needle.length;
  const haystack = caseInsensitive ? lowerText : text;
  const target = caseInsensitive ? lowerNeedle : needle;
  if (!target) return out;

  let from = 0;
  for (;;) {
    const idx = haystack.indexOf(target, from);
    if (idx === -1) break;
    out.push([idx, idx + target.length]);
    from = idx + target.length;
  }
  return out;
}

// Se exporta para que TODO resaltado de búsqueda del helpdesk (hilo y lista
// global) use el mismo tinte: dos tokens distintos para el mismo concepto
// hacían que la misma búsqueda se pintara de dos colores.
export const MARK_BASE =
  "rounded-[3px] px-0.5 bg-status-warning-soft text-fg";
// La coincidencia activa se invierte (fg/bg) en vez de teñirse de marca: así
// se distingue del resto también sobre la burbuja naranja del agente, donde un
// resalte brand-500 sería invisible.
const MARK_ACTIVE = "bg-fg text-bg";

function renderTextNode(value, ctx, key) {
  const start = ctx.pos;
  const end = start + value.length;
  ctx.pos = end;

  const { matches } = ctx;
  if (!matches.length) return value;

  const parts = [];
  let cursor = start;
  for (let m = 0; m < matches.length; m += 1) {
    const [ms, me] = matches[m];
    if (me <= start) continue;
    if (ms >= end) break;
    const s = ms > start ? ms : start;
    const e = me < end ? me : end;
    if (s > cursor) parts.push(value.slice(cursor - start, s - start));
    const globalIndex = ctx.base + m;
    parts.push(
      <mark
        key={`${key}-m${m}`}
        data-hd-match={globalIndex}
        className={cn(MARK_BASE, globalIndex === ctx.activeIndex && MARK_ACTIVE)}
      >
        {value.slice(s - start, e - start)}
      </mark>,
    );
    cursor = e;
  }
  if (!parts.length) return value;
  if (cursor < end) parts.push(value.slice(cursor - start));
  return <React.Fragment key={key}>{parts}</React.Fragment>;
}

function renderNodes(nodes, ctx, keyPrefix) {
  return nodes.map((node, i) => {
    const key = `${keyPrefix}${i}`;
    if (node.type === "text") return renderTextNode(node.value, ctx, key);
    const children = renderNodes(node.children, ctx, `${key}.`);
    if (node.type === "code") {
      return (
        <code
          key={key}
          // bg-fg/10 (no bg-black/10): el fondo se invierte con el tema, si no
          // el bloque ```código``` desaparecía sobre la burbuja oscura en dark.
          className="rounded bg-fg/10 px-1 py-px font-mono text-[0.92em] text-inherit"
        >
          {children}
        </code>
      );
    }
    if (node.type === "strong") return <strong key={key}>{children}</strong>;
    if (node.type === "em") return <em key={key}>{children}</em>;
    return <s key={key}>{children}</s>;
  });
}

/**
 * Renderiza `text` como JSX.
 * @param {string|null|undefined} text
 * @param {object} [options]
 * @param {boolean} [options.format=true]   - false = solo resaltado, sin marcado WA
 * @param {string}  [options.query=""]      - término a resaltar
 * @param {number}  [options.activeIndex=-1]- índice GLOBAL de la coincidencia activa
 * @param {number}  [options.matchBase=0]   - índice global de la 1ª coincidencia de este texto
 * @returns {React.ReactNode}
 */
export function renderWhatsApp(text, options = {}) {
  if (text === null || text === undefined) return null;
  const str = typeof text === "string" ? text : String(text);
  if (!str) return null;

  const { format = true, query = "", activeIndex = -1, matchBase = 0 } = options;
  const tree = format ? tokenize(str, NO_MARKS) : [{ type: "text", value: str }];
  const plain = format ? plainOf(tree) : str;
  const matches = query ? findMatches(plain, query) : NO_MATCHES;
  const ctx = { pos: 0, matches, activeIndex, base: matchBase };
  return renderNodes(tree, ctx, "w");
}

/**
 * Nº de coincidencias de `query`, contadas sobre el MISMO texto plano que usa
 * renderWhatsApp (de ahí que reciba `format`): el contador "3 de 12" y los
 * <mark> pintados nunca se desincronizan.
 * @param {string|null|undefined} text
 * @param {string} query
 * @param {boolean} [format=true]
 * @returns {number}
 */
export function countWhatsAppMatches(text, query, format = true) {
  if (!text || !query) return 0;
  const str = typeof text === "string" ? text : String(text);
  const plain = format ? plainOf(tokenize(str, NO_MARKS)) : str;
  return findMatches(plain, query).length;
}

/**
 * Texto sin los delimitadores de WhatsApp (útil para previews y tests).
 * @param {string|null|undefined} text
 * @returns {string}
 */
export function toPlainText(text) {
  if (text === null || text === undefined) return "";
  const str = typeof text === "string" ? text : String(text);
  if (!str) return "";
  return plainOf(tokenize(str, NO_MARKS));
}

/**
 * Envoltorio de conveniencia para usar el parser como componente.
 */
export function WhatsAppText({ text, format, query, activeIndex, matchBase, className }) {
  return (
    <span className={className}>
      {renderWhatsApp(text, { format, query, activeIndex, matchBase })}
    </span>
  );
}

WhatsAppText.displayName = "WhatsAppText";

export default renderWhatsApp;
